import type { Feature, FeatureCollection, Point } from 'geojson';

import { sourceIndexOf } from './traces-geometry-layer';

/**
 * Rejection/precision markers on top of the map editor and the read-only map (app#287 Task 8,
 * F4). Pure helpers — no Mapbox import — so they run without a browser/WebGL context.
 *
 * Both TRACES rejection items and geo-service `PRECISION_*` findings resolve to the same
 * intermediate shape, `LocatedFinding`, which is what actually indexes into the geometry and
 * carries the point. Keeping the two producer functions (`tracesFindingsToLocated`,
 * `precisionFindingsToLocated`) separate from the two consumer functions (`outlineFeatures`,
 * `pointFeatures`) is what lets a single pair of Mapbox layers per kind render either source.
 */

/** TRACES place is the 1-based production-place index; `featureIndex` here is always 0-based. */
export interface LocatedFinding {
  featureIndex: number;
  at?: [number, number];
  /** Only set for precision findings — drives the stronger-vs-weaker styling. */
  kind?: 'repaired' | 'collapsed';
}

/** `CompanySite.tracesFinding` / `TracesProduct.lastRejection` entry — see `RejectionItem` in model.ts. */
export interface TracesFindingItem {
  code: string;
  message?: string;
  place?: number;
  sourceFeature?: number | null;
  point?: [number, number];
}

/** The geo-service codes the violet layer renders (app#315 covers the rest). */
export type PrecisionCode = 'PRECISION_REPAIRED' | 'PRECISION_COLLAPSED' | 'HOLES_UNSUPPORTED' | 'REPAIR_SPLIT';

const PRECISION_KIND: Record<PrecisionCode, 'repaired' | 'collapsed'> = {
  PRECISION_REPAIRED: 'repaired',
  PRECISION_COLLAPSED: 'collapsed',
  HOLES_UNSUPPORTED: 'collapsed',
  REPAIR_SPLIT: 'collapsed',
};

export function isPrecisionCode(code: string): code is PrecisionCode {
  return Object.hasOwn(PRECISION_KIND, code);
}

export function hasTracesShapeFinding(findings: GeoCheckFinding[]): boolean {
  return (findings ?? []).some(({ code }) => code === 'HOLES_UNSUPPORTED' || code === 'REPAIR_SPLIT');
}

/** `CompanySite.geoCheckFindings` entry, the subset this module cares about. */
export interface GeoCheckFinding {
  code: string;
  featureIndex?: number;
  sourceFeature?: number | null;
  at?: [number, number];
}

export const TRACES_AMBER = '#ff9100';
export const PRECISION_VIOLET = '#7c4dff';

/**
 * TRACES' `place` is 1-based; an item without `place` cannot be outlined but may still have a point.
 * A rejection stored without `sourceFeature` keeps `place - 1` — it is never re-mapped through the
 * CURRENT TRACES geometry, which need not be the one that was sent.
 */
export function tracesFindingsToLocated(items: TracesFindingItem[]): LocatedFinding[] {
  return (items ?? []).map((item) => ({
    featureIndex: item.sourceFeature ?? (item.place != null ? item.place - 1 : -1),
    at: item.point,
  }));
}

function originalIndex(
  sourceFeature: number | null | undefined,
  derivedIndex: number,
  traces: FeatureCollection | undefined
): number {
  if (sourceFeature != null) return sourceFeature;
  return derivedIndex < 0 ? derivedIndex : sourceIndexOf(traces, derivedIndex);
}

/**
 * Filters to the TRACES-derivation codes (`PRECISION_*`, `HOLES_UNSUPPORTED`, `REPAIR_SPLIT`) —
 * every other geo-service code is out of scope (app#315) and must never reach the map. Findings
 * without a `featureIndex` are dropped: there is nothing to locate.
 */
export function precisionFindingsToLocated(findings: GeoCheckFinding[], traces?: FeatureCollection): LocatedFinding[] {
  return (findings ?? [])
    .filter(
      (f): f is GeoCheckFinding & { featureIndex: number; code: PrecisionCode } =>
        isPrecisionCode(f.code) && f.featureIndex != null
    )
    .map((f) => ({
      featureIndex: originalIndex(f.sourceFeature, f.featureIndex, traces),
      at: f.at,
      kind: PRECISION_KIND[f.code],
    }));
}

/**
 * One outline feature per located finding whose `featureIndex` resolves into `geo.features` —
 * an out-of-range index (a stale reference, a shorter geometry than TRACES saw) is skipped, never
 * thrown, so a bad index degrades to "point only" instead of breaking the whole layer.
 */
export function outlineFeatures(geo: FeatureCollection | undefined, located: LocatedFinding[]): Feature[] {
  const features = geo?.features ?? [];
  const out: Feature[] = [];
  for (const l of located) {
    const source = features[l.featureIndex];
    if (source?.geometry)
      out.push({ type: 'Feature', properties: { kind: l.kind ?? null }, geometry: source.geometry });
  }
  return out;
}

/** One point feature per located finding that actually carries a point. */
export function pointFeatures(located: LocatedFinding[]): Feature<Point>[] {
  return located
    .filter((l): l is LocatedFinding & { at: [number, number] } => l.at != null)
    .map((l) => ({
      type: 'Feature',
      properties: { kind: l.kind ?? null },
      geometry: { type: 'Point', coordinates: l.at },
    }));
}

/** Amber TRACES layer paint — `circle`/`line` only (the e2e Mapbox stub breaks on `symbol`). */
export function tracesLayerPaint(stale: boolean): { circle: object; line: object } {
  const opacity = stale ? 0.4 : 1;
  return {
    circle: {
      'circle-radius': 7,
      'circle-color': TRACES_AMBER,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
      'circle-opacity': opacity,
      'circle-stroke-opacity': opacity,
    },
    line: { 'line-color': TRACES_AMBER, 'line-width': 3, 'line-opacity': opacity },
  };
}

/**
 * Violet precision layer paint — deliberately not amber (TRACES) or red (the editor's own
 * checks). `PRECISION_COLLAPSED` renders larger and fully opaque, `PRECISION_REPAIRED` smaller
 * and dimmer, driven by the `kind` property `outlineFeatures`/`pointFeatures` stamp on.
 */
export function precisionLayerPaint(): { circle: object; line: object } {
  return {
    circle: {
      'circle-radius': ['match', ['get', 'kind'], 'collapsed', 9, 6],
      'circle-color': PRECISION_VIOLET,
      'circle-opacity': ['match', ['get', 'kind'], 'collapsed', 1, 0.55],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    },
    line: {
      'line-color': PRECISION_VIOLET,
      'line-width': ['match', ['get', 'kind'], 'collapsed', 3, 1.5],
      'line-opacity': ['match', ['get', 'kind'], 'collapsed', 1, 0.55],
    },
  };
}
